import React, {useCallback, useEffect, useMemo} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {Controller, ControllerRenderProps, FieldError, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import {BackTo} from 'components/common/BackTo';
import {ButtonVariant} from 'components/ui-kit/Button';
import {Input} from 'components/ui-kit/Input';
import {ContentContainer} from 'components/ui-kit/PageComponents';
import {SelectOption} from 'components/ui-kit/Select';
import {history} from 'core/history';
import {ROUTE} from 'core/routes';
import {editUserData$, updateUserFx} from 'models/profile/staff/table';
import {EmployeeRegistrationRequest, EmployeeUpdateRequest} from 'types';

import {Actions, BackButton, CustomTitle, Field, FormFields, SubmitButton, Wrapper} from './styled';

type FormField = {
    name: keyof EmployeeRegistrationRequest;
    readonly?: boolean;
    options?: SelectOption<string>[];
};

export const UpdateUser = () => {
    const {t, i18n} = useTranslation();
    const translateValidates = useCallback(
        (key: string) => t(`pages.register.validations.${key}`),
        [i18n.language],
    );
    const translateForm = useCallback((key: string) => t('pages.register.fields.' + key), [
        i18n.language,
    ]);

    const editUserData = useStore(editUserData$);
    useEffect(() => {
        if (!editUserData) return history.push(ROUTE.STAFF);

        setValue('first_name', editUserData.firstName);
        setValue('middle_name', editUserData.middleName);
        setValue('last_name', editUserData.lastName);
        setValue('phone', editUserData.phone);
    }, []);

    const schema = useMemo(
        () =>
            yup.object().shape({
                first_name: yup.string().required(translateValidates('required')),
                middle_name: yup.string().notRequired(),
                last_name: yup.string().required(translateValidates('required')),
                phone: yup
                    .string()
                    .transform((value) => (value === '' ? null : value))
                    .nullable()
                    .notRequired()
                    .matches(/^\+?\d{10,15}$/, translateValidates('phone')),
            }),
        [translateValidates],
    );

    const {
        control,
        formState: {errors},
        handleSubmit,
        setValue,
        reset,
    } = useForm<EmployeeRegistrationRequest>({
        resolver: yupResolver(schema),
        mode: 'all',
    });

    const onSubmit = async (data: EmployeeRegistrationRequest) => {
        try {
            if (!editUserData?.id) return;
            const payload: EmployeeUpdateRequest & {id: string | number} = {
                firstName: {value: data.first_name, reset: false},
                middleName: {value: data.middle_name || '', reset: false},
                lastName: {value: data.last_name, reset: false},
                phone: {value: data.phone || '', reset: false},
                id: editUserData.id,
            };

            await updateUserFx(payload);
            reset();
            history.push(ROUTE.STAFF);
        } catch (error) {
            console.log(error);
        }
    };

    const formFields: FormField[] = useMemo(
        () => [{name: 'first_name'}, {name: 'middle_name'}, {name: 'last_name'}, {name: 'phone'}],
        [t],
    );

    const controlRender = (
        controlEvents: ControllerRenderProps<EmployeeRegistrationRequest>,
        field: FormField,
    ) => {
        const baseProps = {
            placeholder: translateForm(field.name),
            hasError: !!errors?.[field.name],
            disabled: field.readonly,
        };

        const props = {...baseProps, ...controlEvents};
        return <Input {...props} />;
    };

    return (
        <>
            <BackTo to={ROUTE.STAFF}>{t('pages.profile.staff.create.back')}</BackTo>
            <ContentContainer>
                <Wrapper>
                    <form onSubmit={handleSubmit(onSubmit)} style={{marginTop: 50}}>
                        <CustomTitle size={4}>{t('pages.profile.staff.update.title')}</CustomTitle>
                        <FormFields>
                            {formFields.map((field) => (
                                <Field
                                    key={field.name}
                                    text={(errors?.[field.name] as FieldError)?.message}
                                    isError={!!errors?.[field.name]}
                                >
                                    <span>{t(`pages.register.fields.${field.name}`)}</span>
                                    <Controller
                                        name={field.name}
                                        control={control}
                                        defaultValue=""
                                        render={(props) => controlRender(props, field)}
                                    />
                                </Field>
                            ))}
                        </FormFields>
                        <Actions>
                            <BackButton
                                type="button"
                                variant={ButtonVariant.Outline}
                                onClick={() => {
                                    history.push(ROUTE.STAFF);
                                    reset();
                                }}
                            >
                                {t('pages.profile.staff.create.cancel')}
                            </BackButton>
                            <SubmitButton type="submit">
                                {t('pages.profile.staff.update.updateBtn')}
                            </SubmitButton>
                        </Actions>
                    </form>
                </Wrapper>
            </ContentContainer>
        </>
    );
};
